import React from "react";
import { motionSafe as motion } from "@/lib/motionSafe";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { SEO } from "../components/site/SEO";
import { useI18n } from "../i18n";

export const LegalPage = ({ data, testId, path }) => {
  const { lang } = useI18n();

  const back = { de: "Zurück zur Startseite", en: "Back to home" };  return (
    <div className="min-h-screen bg-[#030712] text-white">
      <SEO
        path={path}
        title={`${data.title.de} | NexTech.Law`}
        description={data.note?.de || `${data.title.de} — NexTech.Law`}
      />
      <Header />
      <main data-testid={testId} className="pt-28 md:pt-36 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 1, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to="/"
              data-testid="legal-back-home"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} /> <span>{lang === "en" ? back.en : back.de}</span>
            </Link>

            <div className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#3B82F6] mb-4">
              NexTech.Law
            </div>
            <h1 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl leading-[0.98] text-white">
              {data.title.de}
              <span className="block text-white/40 text-3xl md:text-5xl mt-2">
                {data.title.en}
              </span>
            </h1>
            {data.note && (
              <p className="mt-6 font-mono-tech text-xs text-gray-400 max-w-3xl">
                <span className="text-gray-400">{data.note.de}</span>
                <span className="mx-2 text-gray-400">/</span>
                <span>{data.note.en}</span>
              </p>
            )}
          </motion.div>

          {/* Sections */}
          <div className="mt-16 md:mt-20 space-y-14 md:space-y-16">
            {data.sections.map((s, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 1, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`legal-section-${i}`}
                className="border-t border-white/10 pt-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                  {/* DE */}
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#3B82F6] mb-3">
                      Deutsch
                    </div>
                    <h2 className="font-display text-xl md:text-2xl font-bold text-white leading-snug">
                      {s.de.title}
                    </h2>
                    <div className="mt-4 text-sm md:text-base text-gray-300 leading-relaxed whitespace-pre-line">
                      {s.de.body}
                    </div>
                  </div>
                  {/* EN */}
                  <div className="md:border-l md:border-white/10 md:pl-10">
                    <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-3">
                      English
                    </div>
                    <h2 className="font-display text-xl md:text-2xl font-bold text-white/80 leading-snug">
                      {s.en.title}
                    </h2>
                    <div className="mt-4 text-sm md:text-base text-gray-400 leading-relaxed whitespace-pre-line">
                      {s.en.body}
                    </div>
                  </div>
                </div>
              </motion.section>
            ))}

            {data.sections.length === 0 && (
              <div className="border-t border-white/10 pt-16 text-center">
                <div className="font-display text-2xl text-white/70 mb-3">
                  {data.note?.de}
                </div>
                <div className="font-mono-tech text-xs text-gray-400">
                  {data.note?.en}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
